/**
 * 代投保功能 Hook
 * 用于管理代投保企业选择和请求头注入
 *
 * 注意：代投保企业的判断现在通过后端返回的 userInfo.isAgent 字段来确定
 */
import { ref } from 'vue'
import { openDialog } from '@stroybrooke-business-tools/components'
import SelectEnterpriseDialog from '@/components/SelectEnterpriseDialog.vue'
import { getBackstageOnlineEditEnterpriseId } from '@/utils/backstageOnlineEdit'

/** 代投保企业信息接口 */
export interface ProxyEnterpriseInfo {
  /** 企业 ID */
  id: string
  /** 企业名称 */
  name: string
  /** 统一社会信用代码 */
  creditCode?: string
  /** 企业地址 */
  address?: string
}

// 全局存储代投保企业信息
const proxyEnterpriseInfo = ref<ProxyEnterpriseInfo | null>(null)

// localStorage key
const STORAGE_KEY = 'proxy_insurance_enterprise'

/**
 * 代投保功能 Hook
 */
export const useProxyInsurance = () => {
  /**
   * 获取当前代投保企业信息
   * @returns 企业信息对象，如果未设置或非代投保企业则返回 null
   */
  const getApprovalAgent = (): ProxyEnterpriseInfo | null => {
    // 从 localStorage 恢复
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        proxyEnterpriseInfo.value = parsed
        return parsed
      }
    } catch (error) {
      console.error('读取代投保企业信息失败:', error)
    }

    return null
  }

  /**
   * 获取代投保请求头对象
   * @returns 包含 approval_agent 字段的对象，可直接展开到请求头中
   */
  const getApprovalAgentHeader = (): Record<string, string> => {
    const backstageEnterpriseId = getBackstageOnlineEditEnterpriseId()
    if (backstageEnterpriseId) {
      return { approval_agent: backstageEnterpriseId }
    }

    const info = getApprovalAgent()
    if (info?.id) {
      return { approval_agent: info.id }
    }
    return {}
  }

  /**
   * 设置代投保企业信息
   * @param info 企业信息对象
   */
  const setApprovalAgent = (info: ProxyEnterpriseInfo): void => {
    proxyEnterpriseInfo.value = info
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(info))
    } catch (error) {
      console.error('保存代投保企业信息失败:', error)
    }
  }

  /**
   * 清除代投保企业信息
   */
  const clearApprovalAgent = (): void => {
    proxyEnterpriseInfo.value = null
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (error) {
      console.error('清除代投保企业信息失败:', error)
    }
  }

  /**
   * 打开企业选择弹窗
   * @returns 选择的企业信息，如果取消则返回 null
   */
  const showSelectEnterpriseDialog =
    async (): Promise<ProxyEnterpriseInfo | null> => {
      try {
        const result = (await openDialog(
          SelectEnterpriseDialog,
        )) as ProxyEnterpriseInfo
        if (result && result.id) {
          setApprovalAgent(result)
          return result
        }
        return null
      } catch (error) {
        // 用户取消选择
        console.log('用户取消选择企业')
        return null
      }
    }

  /**
   * 初始化代投保功能
   * 在应用启动时调用
   * - 如果当前用户是代投保企业（isAgent=true）且未设置代投保企业，则弹出选择弹窗
   *
   * @param userInfo 用户信息对象，需包含 isAgent 字段
   */
  const initProxyInsurance = async (
    userInfo: { isAgent?: boolean } = {},
  ): Promise<void> => {
    // 通过 isAgent 字段判断是否是代投保企业
    const existing = getApprovalAgent()
    if (userInfo?.isAgent && !existing) {
      await showSelectEnterpriseDialog()
      window.location.reload()
    }
  }

  return {
    /** 获取代投保企业信息 */
    getApprovalAgent,
    /** 获取代投保请求头对象 */
    getApprovalAgentHeader,
    /** 设置代投保企业信息 */
    setApprovalAgent,
    /** 清除代投保企业信息 */
    clearApprovalAgent,
    /** 打开企业选择弹窗 */
    showSelectEnterpriseDialog,
    /** 初始化代投保功能 */
    initProxyInsurance,
    /** 响应式的企业信息（只读） */
    proxyEnterpriseInfo,
  }
}
